import { api } from './client'
import type { Style, StyleCategory } from '../data/styles'

interface StylesApiResponse {
	styles: Array<{
		id: string
		title: string
		previewUrl: string
		previewUrlVariants: Record<string, string> | null
		modelId: string
		modelAdapter: string
		modelName: string
		prompt: string
		/** Гость: бэк вырезал prompt и выставил флаг (get_styles). */
		promptLocked?: boolean
		settings: {
			aspectRatio?: string
			resolution?: string
			duration?: string
			outputFormat?: string
		} | null
		isVideo: boolean
		categorySlug: string | null
		categorySlugs: string[] | null
		categories: Array<{
			slug: string
			isVideo: boolean
			defaultMediaUrl: string | null
		}> | null
		isPopular: boolean
		popularSort?: number | null
		usageCount: number
		sortOrder: number
		// Наглядное демо «до/после»: исходник + вариации силы эффекта
		demoBeforeUrl?: string | null
		demoVariants?: Array<{
			key: string
			label: string
			after_url: string
			prompt_patch: string
		}>
		// Поля «инструмента» (см. Style в data/styles.ts)
		priceOverride?: number | null
		discountPrice?: number | null
		discountUntil?: string | null
		referenceVideoUrl?: string | null
		userVideoLocked?: boolean
		referenceAudioUrl?: string | null
		userAudioLocked?: boolean
		// Признак «референс есть» без ссылки (19.09): сами *Url из публичной выдачи
		// убраны, файл подставляет сервер, на просмотр даётся короткая подпись.
		hasReferenceVideo?: boolean
		hasReferenceAudio?: boolean
	}>
	categories: Array<CategoryApiItem>
}

interface CategoryApiItem {
	slug: string
	label: string
	description: string | null
	previewUrl: string | null
	isVideo: boolean
	sortOrder: number
	children?: CategoryApiItem[]
}

export async function fetchStyles(): Promise<{
	styles: Style[]
	categories: StyleCategory[]
}> {
	const data = await api.request<StylesApiResponse>('/api/v1/styles')

	const styles: Style[] = data.styles.map((s) => ({
		id: s.id,
		title: s.title,
		previewUrl: s.previewUrl,
		previewUrlVariants: s.previewUrlVariants || undefined,
		modelId: s.modelId,
		modelAdapter: s.modelAdapter,
		modelName: s.modelName,
		prompt: s.prompt,
		promptLocked: s.promptLocked,
		settings: s.settings || undefined,
		isVideo: s.isVideo,
		categorySlug: s.categorySlug || undefined,
		categorySlugs: s.categorySlugs || undefined,
		categories: s.categories
			? s.categories.map((c) => ({
				slug: c.slug,
				isVideo: c.isVideo,
				defaultMediaUrl: c.defaultMediaUrl,
			}))
			: undefined,
		isPopular: s.isPopular,
		popularSort: s.popularSort ?? null,
		usageCount: s.usageCount,
		sortOrder: s.sortOrder,
		isActive: true,
		demoBeforeUrl: s.demoBeforeUrl || undefined,
		demoVariants: s.demoVariants?.length ? s.demoVariants : undefined,
		priceOverride: s.priceOverride || undefined,
		discountPrice: s.discountPrice || undefined,
		discountUntil: s.discountUntil || undefined,
		referenceVideoUrl: s.referenceVideoUrl || undefined,
		userVideoLocked: s.userVideoLocked || undefined,
		referenceAudioUrl: s.referenceAudioUrl || undefined,
		userAudioLocked: s.userAudioLocked || undefined,
		hasRefVideo: s.hasReferenceVideo || undefined,
		hasRefAudio: s.hasReferenceAudio || undefined,
	}))

	const mapCategory = (c: CategoryApiItem): StyleCategory => ({
		slug: c.slug,
		label: c.label,
		description: c.description || undefined,
		previewUrl: c.previewUrl || undefined,
		isVideo: c.isVideo,
		sortOrder: c.sortOrder,
		children: c.children?.map(mapCategory),
	})

	const categories: StyleCategory[] = data.categories.map(mapCategory)

	return { styles, categories }
}
